import { getSkill, skillStepLockMs, type SkillDef } from '@jianghu/game-data';
import * as THREE from 'three';
import type { SfxName } from '../audio/audio';
import type { EntityView } from '../entities/entity-view';
import type { Vfx } from '../vfx/vfx';

export interface FxContext {
  vfx: Vfx;
  schedule(delaySec: number, fn: () => void): void;
  sfx(name: SfxName, pos?: THREE.Vector3, volume?: number): void;
  /** Screen shake; when `at` is given it is attenuated by distance from the local player. */
  shake(amount: number, at?: THREE.Vector3): void;
  /** True when the effect belongs to the local player (louder, camera shake). */
  isSelf: boolean;
}

const tmp = new THREE.Vector3();

/** Animation clip name for a technique step (used by prediction and by remote playback). */
export function castAnimation(skill: SkillDef, step: number): string {
  if (skill.combo?.length) return skill.combo[step % skill.combo.length]?.animation ?? 'ATTACK_1';
  return skill.animation;
}

/**
 * Plays the animation and visual/audio effects of a technique on an entity view. Timings come from
 * the skill data (cast time = release), so visuals line up with the server's hit resolution.
 */
export function playCastFx(
  view: EntityView,
  skillId: string,
  step: number,
  aimX: number,
  aimZ: number,
  castMs: number,
  ctx: FxContext,
): void {
  const skill = getSkill(skillId);
  if (!skill) return;
  const lockSec = skillStepLockMs(skill, step) / 1000;
  const release = castMs / 1000;
  const anim = castAnimation(skill, step);
  view.playAction(anim, lockSec);
  const yaw = Math.atan2(aimX, aimZ);
  const color = '#f2c96b';
  switch (skillId) {
    case 'wudang_sword_combo': {
      ctx.schedule(Math.max(0, release - 0.06), () => {
        ctx.sfx(step === 2 ? 'sword_swing_heavy' : 'sword_swing', view.pos);
        ctx.vfx.swordArc(view.pos, yaw, {
          color,
          radius: step === 2 ? 3.0 : 2.7,
          arcDeg: step === 2 ? 250 : 150,
          reverse: step === 1,
          tilt: step === 0 ? 0.35 : step === 1 ? -0.3 : 0.05,
          sweep: step === 2 ? 0.2 : 0.12,
          height: step === 2 ? 0.95 : 1.1,
        });
        if (step === 2) {
          ctx.vfx.dust(view.pos, 12);
          if (ctx.isSelf) ctx.shake(0.15);
        }
      });
      return;
    }
    case 'wudang_cloud_parting_strike':
      ctx.vfx.charge(
        () => (view.dead ? null : (view.weaponTip(tmp)?.clone() ?? view.chestPos(new THREE.Vector3()))),
        color,
        release,
      );
      ctx.schedule(release, () => {
        ctx.sfx('skill_thrust', view.pos);
        const from = view.chestPos(new THREE.Vector3());
        ctx.vfx.streak(from, new THREE.Vector3(aimX, 0, aimZ), 4.2, '#ffd77a', 0.42);
        ctx.vfx.flash(from.clone().add(new THREE.Vector3(aimX * 3, 0, aimZ * 3)), '#fff1c0', 2.2, 0.2);
        if (ctx.isSelf) ctx.shake(0.12);
      });
      return;
    case 'wudang_sword_qi_crescent':
      ctx.vfx.charge(
        () => (view.dead ? null : (view.weaponTip(tmp)?.clone() ?? view.chestPos(new THREE.Vector3()))),
        color,
        release,
      );
      ctx.schedule(Math.max(0, release - 0.05), () => {
        ctx.sfx('skill_wave', view.pos);
        ctx.vfx.swordArc(view.pos, yaw, { color, radius: 2.4, arcDeg: 120, tilt: -1.1, sweep: 0.1 });
      });
      return;
    case 'wudang_taiji_formation':
      ctx.vfx.taiji(view.pos.clone(), 5.5, release, '#ffd77a');
      ctx.schedule(release, () => {
        ctx.sfx('skill_formation', view.pos);
        if (ctx.isSelf) ctx.shake(0.4);
      });
      return;
    case 'wolf_bite':
      ctx.schedule(Math.max(0, release - 0.05), () => {
        ctx.sfx('wolf_bite', view.pos, 0.8);
        ctx.vfx.swordArc(view.pos, yaw, {
          color: '#ff7a5a',
          radius: 1.7,
          width: 0.5,
          arcDeg: 90,
          sweep: 0.08,
          height: 0.65,
        });
      });
      return;
    case 'bandit_slash':
      ctx.schedule(Math.max(0, release - 0.06), () => {
        ctx.sfx('sword_swing', view.pos, 0.8);
        ctx.vfx.swordArc(view.pos, yaw, {
          color: '#ff6a4a',
          radius: 2.3,
          arcDeg: 130,
          tilt: 0.5,
          sweep: 0.12,
        });
      });
      return;
    case 'bandit_heavy_chop':
      ctx.schedule(release, () => {
        ctx.sfx('heavy_impact', view.pos);
        const front = view.pos.clone().add(new THREE.Vector3(aimX * 1.8, 0, aimZ * 1.8));
        ctx.vfx.dust(front, 26);
        ctx.vfx.shockwave(front, 2.4, '#ff8a5a', 0.35);
        ctx.shake(0.35, front);
      });
      return;
  }
}
